import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  Stack,
  TextField,
  Typography,
  Card,
  CardContent,
} from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { gql, useMutation } from "@apollo/client";
import { useParams } from "react-router-dom";
import { IMAGES_API_URL } from "./constants";

export default function SearchBadge() {
  const { eventId } = useParams(); // Get eventId from URL params
  const { control, handleSubmit, watch, setError, clearErrors } = useForm();
  const emailValue = watch("email");
  const mobileNumberValue = watch("mobileNumber");

  const [searchId, setSearchId] = useState(null);
  const [showButton, setShowButton] = useState(false);
  const [role, setRole] = useState();
  /*
   * Every badge this person holds, not just the first one.
   *
   * Somebody registered as both a Visitor and a Delegate on the same email has TWO badges, and
   * this screen only ever offered one of them — always the same one, because the server's legacy
   * single-hit answer returns the first match in the event's own role order. The other badge was
   * unreachable from here: no amount of searching would surface it.
   */
  const [choices, setChoices] = useState([]);

  const [searchBadgeFromAllRole, { loading, error }] = useMutation(gql`
    mutation searchBadgefromAllRole($eventId: String!, $searchData: String!) {
      searchBadgefromAllRole(eventId: $eventId, searchData: $searchData, all: true)
    }
  `);

  const handleSearch = async (variables) => {
    if (!variables.email && !variables.mobileNumber) {
      setError("formError", { message: "Please fill in at least one field." });
      return;
    }

    try {
      const searchData = variables.email || variables.mobileNumber;
      const response = await searchBadgeFromAllRole({
        variables: {
          searchData,
          eventId,
        },
      });

      /*
       * With `all: true` the server answers with an ARRAY. It still answers with a bare object to
       * any caller that omits the flag, so both shapes are accepted here rather than assuming the
       * new one — the same normalisation the kiosk does.
       */
      const raw = response.data.searchBadgefromAllRole;
      if (!raw) {
        setChoices([]);
        setShowButton(false);
        return;
      }
      const parsed = JSON.parse(raw);
      const hits = (Array.isArray(parsed) ? parsed : [parsed]).filter(
        (h) => h && h.id && h.role
      );

      if (hits.length === 0) {
        setChoices([]);
        setShowButton(false);
        return;
      }

      setChoices(hits);
      /* One badge downloads as it always did. Several means asking — picking one for somebody
         hands them the wrong badge, and they are the only person who knows which they came for. */
      if (hits.length === 1) {
        setSearchId(hits[0].id);
        setRole(hits[0].role);
      } else {
        setSearchId(null);
        setRole(undefined);
      }
      setShowButton(true);
    } catch (err) {
      console.error("Failed to search for badge.", err);
      setChoices([]);
      setShowButton(false);
    }
  };

  const badgeUrl = (r, id) =>
    `https://api-reg.eventegration.com/graphql/badge/${eventId}/${r}/${id}`;

  const handleDownload = (r = role, id = searchId) => {
    if (!r || !id) return;
    window.open(badgeUrl(r, id));
    setShowButton(null);
  };

  if (!eventId) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
          bgcolor: "#f5f5f5",
          p: 3,
        }}
      >
        <Card
          sx={{
            maxWidth: 400,
            p: 3,
            borderRadius: 3,
            boxShadow: 3,
            textAlign: "center",
          }}
        >
          <Typography
            variant="h5"
            color="error"
            gutterBottom
            sx={{ fontWeight: "bold" }}
          >
            Invalid Link
          </Typography>
          <Typography variant="body1" color="textSecondary">
            The link you followed is incorrect or missing the required event
            information. Please check and try again.
          </Typography>
        </Card>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        minHeight: "100vh",
        bgcolor: "#f5f5f5",
        p: 3,
      }}
    >
      <Card
        sx={{
          maxWidth: 400,
          p: 3,
          borderRadius: 3,
          boxShadow: 3,
        }}
      >
        {!showButton ? (
          <CardContent>
            {eventId && (
              <img
                src={`${IMAGES_API_URL}logo/${eventId}.png`}
                width="100%"
                height={"100px"}
                style={{ objectFit: "contain" }}
                alt="Logo"
                onError={(e) => {
                  e.target.style.display = "none";
                }}
              />
            )}
            <Typography
              variant="h5"
              textAlign="center"
              gutterBottom
              sx={{ fontWeight: "bold", color: "#2a9d8f", mt: 2 }}
            >
              Search Your Badge
            </Typography>
            <Typography variant="subtitle1" textAlign="center" gutterBottom>
              Enter your registered email or mobile number to search.
            </Typography>
            <form
              onSubmit={handleSubmit((data) => {
                clearErrors("formError");
                handleSearch(data);
              })}
            >
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Controller
                    name="email"
                    control={control}
                    defaultValue=""
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Email"
                        type="email"
                        fullWidth
                        sx={{ bgcolor: "#fff" }}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Divider>
                    <Chip label="OR" />
                  </Divider>
                </Grid>
                <Grid item xs={12}>
                  <Controller
                    name="mobileNumber"
                    control={control}
                    defaultValue=""
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Mobile Number"
                        type="number"
                        fullWidth
                        // Owner 2026-09-20: people type the number the way they'd dial it
                        // (+971…), the badge is stored without the country code, and the only
                        // feedback was "Badge not found". Say it on the field itself.
                        placeholder="e.g. 554983387"
                        helperText="Enter mobile number without country code"
                        sx={{ bgcolor: "#fff" }}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Button
                    type="submit"
                    variant="contained"
                    fullWidth
                    sx={{
                      bgcolor: "#2a9d8f",
                      "&:hover": { bgcolor: "#21867a" },
                    }}
                  >
                    Search &nbsp;{" "}
                    {loading && <CircularProgress color="inherit" size={20} />}
                  </Button>
                </Grid>
                {error && (
                  <Stack
                    direction="row"
                    justifyContent="center"
                    sx={{ width: "100%" }}
                  >
                    <Typography textAlign="center" color="error" sx={{ mt: 2 }}>
                      {error.message}
                    </Typography>
                  </Stack>
                )}
                <Grid item xs={12}>
                  <Typography
                    variant="body2"
                    color="error"
                    textAlign="center"
                    sx={{ mt: 2 }}
                  >
                    {control._formState.errors?.formError?.message}
                  </Typography>
                </Grid>
              </Grid>
            </form>
          </CardContent>
        ) : (
          <CardContent>
            <Stack direction="column" justifyContent="center" spacing={3}>
              <Typography
                variant="h5"
                textAlign="center"
                gutterBottom
                sx={{ fontWeight: "bold", color: "#2a9d8f" }}
              >
                {choices.length > 1
                  ? "You have more than one badge"
                  : "Download Your Badge"}
              </Typography>

              {choices.length > 1 ? (
                <>
                  {/* Named, not numbered. "Badge 1 / Badge 2" tells nobody which one they want. */}
                  <Typography textAlign="center" color="textSecondary">
                    This email is registered for {choices.length} badges. Choose
                    the one you need — you can come back for the other.
                  </Typography>
                  {choices.map((c) => (
                    <Button
                      key={`${c.role}-${c.id}`}
                      onClick={() => handleDownload(c.role, c.id)}
                      variant="contained"
                      color="success"
                      fullWidth
                      sx={{ textTransform: "capitalize" }}
                    >
                      Download {c.role} badge
                    </Button>
                  ))}
                </>
              ) : (
                <Button
                  onClick={() => handleDownload()}
                  variant="contained"
                  color="success"
                  fullWidth
                >
                  Download Badge
                </Button>
              )}
            </Stack>
          </CardContent>
        )}
      </Card>
    </Box>
  );
}
